import React, { Children, forwardRef, isValidElement, useId, useMemo, useRef } from "react";
import Select from "react-select";

const textContent = (node) => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textContent).join("");
  if (isValidElement(node)) return textContent(node.props.children);
  return "";
};

const optionsFromChildren = (children) =>
  Children.toArray(children).flatMap((child) => {
    if (!isValidElement(child)) return [];

    if (child.type === React.Fragment) {
      return optionsFromChildren(child.props.children);
    }

    if (child.type === "optgroup") {
      return [{
        label: child.props.label,
        options: optionsFromChildren(child.props.children),
      }];
    }

    if (child.type !== "option") return [];

    const label = child.props.children;
    return [{
      value: child.props.value ?? textContent(label),
      label,
      searchLabel: textContent(label),
      isDisabled: Boolean(child.props.disabled),
    }];
  });

const flattenOptions = (options = []) =>
  options.flatMap((option) => (Array.isArray(option?.options) ? flattenOptions(option.options) : [option]));

const normalizeClassName = (className = "") => {
  const classes = new Set(String(className).split(/\s+/).filter(Boolean));
  classes.add("form-select");
  classes.add("p-0");
  classes.add("akas-select-field");
  return Array.from(classes).join(" ");
};

/**
 * React Select dengan kontrak onChange ala <select> native.
 * Ini membuat migrasi form lama tetap aman: event.target.value dan name masih tersedia.
 */
const AkasSelect = forwardRef(({
  children,
  options: providedOptions,
  value,
  onChange,
  onBlur,
  className,
  classNamePrefix = "akas-select",
  disabled,
  isDisabled,
  name,
  id,
  placeholder,
  required,
  isSearchable = true,
  isClearable = false,
  getOptionLabel,
  noOptionsMessage = () => "Pilihan tidak ditemukan",
  menuPortalTarget,
  menuPosition = "fixed",
  "data-validation-field": validationField,
  "aria-label": ariaLabel,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  ...selectProps
}, forwardedRef) => {
  const generatedId = useId().replace(/:/g, "");
  const selectRef = useRef(null);
  const options = useMemo(
    () => providedOptions || optionsFromChildren(children),
    [children, providedOptions],
  );
  const flatOptions = useMemo(() => flattenOptions(options), [options]);
  const selectedOption = useMemo(
    () => flatOptions.find((option) => String(option?.value ?? "") === String(value ?? "")) || null,
    [flatOptions, value],
  );

  const setRefs = (instance) => {
    selectRef.current = instance;
    if (typeof forwardedRef === "function") forwardedRef(instance);
    else if (forwardedRef) forwardedRef.current = instance;
  };

  const emitNativeChange = (option, actionMeta) => {
    const nextValue = option?.value ?? "";
    onChange?.({
      target: { name, value: nextValue },
      currentTarget: { name, value: nextValue },
    }, option, actionMeta);
  };

  return (
    <div
      className={normalizeClassName(className)}
      data-validation-field={validationField || name || undefined}
      tabIndex={-1}
      onFocus={(event) => {
        if (event.target === event.currentTarget) selectRef.current?.focus?.();
      }}>
      <Select
        {...selectProps}
        ref={setRefs}
        inputId={id || `akas-select-${generatedId}`}
        instanceId={id || `akas-select-${generatedId}`}
        name={name}
        options={options}
        value={selectedOption}
        onChange={emitNativeChange}
        onBlur={onBlur}
        classNamePrefix={classNamePrefix}
        isDisabled={Boolean(disabled || isDisabled)}
        isSearchable={isSearchable}
        isClearable={isClearable}
        getOptionLabel={getOptionLabel || ((option) => option?.searchLabel || textContent(option?.label))}
        placeholder={placeholder || "Pilih opsi"}
        noOptionsMessage={noOptionsMessage}
        menuPortalTarget={menuPortalTarget === undefined && typeof document !== "undefined" ? document.body : menuPortalTarget}
        menuPosition={menuPosition}
        required={required}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
      />
    </div>
  );
});

AkasSelect.displayName = "AkasSelect";

export default AkasSelect;
